import React from 'react';
import { List, CreateButton, TopToolbar, useListContext, EditButton, useRecordContext } from 'react-admin';
import { Typography, Chip, Box, useTheme, Card, CardMedia, CardContent, Button, Link, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import IntroSection from './intro';
import { Link as RouterLink } from 'react-router-dom';
import UploadIcon from '@mui/icons-material/Upload';
import { ShowButton } from 'react-admin';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BoxWine, { defaultWineImage } from './BoxWine';
import BoxCheese, { defaultCheeseImage } from './BoxCheese';
import BoxFlower, { defaultFlowerImage} from './BoxFlower';
import BoxCoffee, { defaultCoffeeImage} from './BoxCoffee';


const UploadButton = ({ record }) => {
    return (
        <Button
            component={RouterLink}
            to={`/provenance/${record.id}/upload`}
            startIcon={<UploadIcon />}
        />
        
    );
};


const TransactionHashBox = ({ transactionHash }) => {
    const truncatedHash = transactionHash !== 'N/A' 
        ? `${transactionHash.slice(0, 6)}...${transactionHash.slice(-4)}`
        : 'N/A';

    const handleCopy = () => {
        navigator.clipboard.writeText(transactionHash);
    };

    return (
        <Box className="space-y-2">
            {transactionHash !== 'N/A' ? (
                <>
                    <Box className="flex items-center space-x-2">
                        <Typography variant="body2" className="font-medium">
                            Smart Contract:
                        </Typography>
                        <Link 
                            href={`https://amoy.polygonscan.com/token/${transactionHash}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                            {truncatedHash}
                            <OpenInNewIcon className="ml-1" sx={{ fontSize: 16 }} />
                        </Link>
                        <IconButton 
                            onClick={handleCopy} 
                            className="p-1"
                            aria-label="Copy contract address"
                        >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    <Box className="flex items-center space-x-2">
                        <Typography variant="body2" className="font-medium">
                            Tokens:
                        </Typography>
                        <Link 
                            href={`https://testnets.opensea.io/assets/amoy/${transactionHash}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                            View on OpenSea
                            <OpenInNewIcon className="ml-1" sx={{ fontSize: 16 }} />
                        </Link>
                    </Box>
                </>
            ) : (
                <Typography variant="body2" className="font-medium">
                    {/* No minted tokens. */}
                </Typography>
            )}
        </Box>
    );
};


const ProtectedInfo = ({ record }) => {
    const theme = useTheme();

    const tagModel = record?.tag_model || 'N/A';
    const isTagTamper = record?.is_tag_tamper ?? 'N/A';
    const tagsCount = record?.tags_count ?? 'N/A';
    const transactionHash = record?.contract_address || 'N/A';

    return (
        <Box className="protected-info" 
            sx={{ 
                mt: 2, 
                p: 2, 
                borderRadius: 1,
                backgroundColor: theme.palette.mode === 'dark' 
            ? '#0f1d1d !important'
            : '#EFF4E5 !important',
        border: theme.palette.mode === 'dark'
            ? '1px solid #1a2a2a !important'
            : '1px solid #d4e6d4 !important'
            }}
        >
            <Typography 
                variant="subtitle1" 
                sx={{ 
                    mb: 2, 
                    color: theme.palette.mode === 'dark'
                        ? theme.palette.secondary.light
                        : theme.palette.secondary.main,
                    paddingBottom: 0,
                    display: 'inline-block'
                }}
            >
                Protected by:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="body2"><strong>Tag model:</strong> {tagModel}</Typography>
                </Box>
                <Box>
                    <Typography variant="body2"><strong>Tag ID records:</strong> {tagsCount}</Typography>
                </Box>
                <Box>
                    {isTagTamper !== 'N/A' ? (
                        <Chip 
                            label={isTagTamper ? 'Tamper Detect' : 'No Tamper Detect'} 
                            size="small" 
                            color={isTagTamper ? 'success' : 'error'}
                            sx={{
                                bgcolor: isTagTamper 
                                    ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                                    : alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
                                color: isTagTamper
                                    ? theme.palette.success.main
                                    : theme.palette.error.main,
                                '& .MuiChip-label': {
                                    color: 'inherit',
                                }
                            }}
                        />
                    ) : (
                        <Typography variant="body2" component="span">N/A</Typography>
                    )}
                </Box>
            </Box>
            <TransactionHashBox transactionHash={transactionHash} />
        </Box>
    );
};

const ProvenanceItem = ({ record }) => {
    // Get default image based on product type
    const getDefaultImage = () => {
        switch(record?.productType) {
            case 'cheese':
                return defaultCheeseImage;
            case 'coffee':
                return defaultCoffeeImage;
            case 'flower':
                return defaultFlowerImage;
            case 'wine':
            default:
                return defaultWineImage;
        }
    };

    const theme = useTheme();

    if (!record) {
        console.error('ProvenanceItem received an undefined record');
        return null;
    }

    return (
        <Card sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            mb: 2, 
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            position: 'relative'  // Add position relative to allow absolute positioning inside
        }}>
            <CardContent sx={{ 
                p: 2, pb: 6
            }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <Box sx={{ // Image box
                        width: { xs: '100%', sm: 200 },
                        height: { xs: 'auto', sm: 300 },
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        borderRadius: 1,
                    }}>
                        <CardMedia
                            component="img"
                            sx={{ // Image styling
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                maxHeight: { xs: 300, sm: '100%' },
                            }}
                            image={(record?.image) ? record.image : getDefaultImage()}
                            alt={record?.name || 'N/A'}
                        />
                    </Box>
                    {/* Product specific content */}
                    {(!record?.productType || record?.productType === 'wine') && <BoxWine record={record} />}
                    {record?.productType === 'cheese' && <BoxCheese record={record} />}
                    {record?.productType === 'flower' && <BoxFlower record={record} />}
                    {record?.productType === 'coffee' && <BoxCoffee record={record} />}
                </Box>
                <Box sx={{ mt: 2 }}>
                    <ProtectedInfo record={record} />
                </Box>
            </CardContent>
            <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 1 }}>
                <ShowButton record={record} label={false} />
                <EditButton record={record} label={false} />
                <UploadButton record={record} label={false} />
            </Box>
        </Card>
    );
    
};


const ProvenanceListView = () => {
    const { data, isLoading, error } = useListContext();

    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error.message}</div>;
    }

    if (!data || data.length === 0) {
        return <div>No records found</div>;
    }

    return (
        <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
            <Box sx={{ p: { xs: 0, sm: 2 } }}>
                <IntroSection />
                {data.map((record, index) => (
                    <ProvenanceItem key={record.id || index} record={record} />
                ))}
            </Box>
        </Box>
    );
};

// Custom actions component with two create buttons
const ProvenanceListActions = () => (
    <TopToolbar>
      <CreateButton label="Create Wine" to="/provenance/create?productType=wine" />
      {/* <CreateButton label="Create Spirit" to="/provenance/create?productType=spirit" />
      <CreateButton label="Create Cheese" to="/provenance/create?productType=cheese" />
      <CreateButton label="Create Coffee" to="/provenance/create?productType=coffee" />
      <CreateButton label="Create Flowers" to="/provenance/create?productType=flower" /> */}
    </TopToolbar>
  );

const ProvenanceList = props => (
    <List actions={<ProvenanceListActions />} {...props}>
        <ProvenanceListView />
    </List>
);

export { ProtectedInfo };
export default ProvenanceList;